import { useDisclosure } from "@heroui/react";
import { useRef, useState } from "react";
import { type BridgeFormData, useBridgeClaimMutation, useBridgeMutation } from "../../mutations/bridge";
import type {
  BridgeClaimParams,
  BridgeTxClaimedFailed,
  BridgeTxClaimedSuccess,
  BridgeTxUnclaimedFailed,
  BridgeTxUnclaimedSuccess,
} from "../../state/types";
import { BridgeFailedModal } from "./BridgeFailedModal";
import { BridgeForm } from "./BridgeForm";
import { BridgeReviewModal } from "./BridgeReviewModal";
import { BridgeSuccessModal } from "./BridgeSuccessModal";
import { BridgeSuccessToast } from "./BridgeSuccessToast";
import { BridgeWaitingModal } from "./BridgeWaitingModal";
import { BridgeWaitingToast } from "./BridgeWaitingToast";
import { ClaimFailedModal } from "./ClaimFailedModal";
import { ClaimSuccessModal } from "./ClaimSuccessModal";
import { ClaimWaitingModal } from "./ClaimWaitingModal";

export default function Bridge() {
  const bridgeFormDataRef = useRef<BridgeFormData | null>(null);
  // Unclaimed Tx result
  const unclaimedSuccessTxDataRef = useRef<BridgeTxUnclaimedSuccess | null>(null);
  const unClaimedFailedTxDataRef = useRef<BridgeTxUnclaimedFailed | null>(null);
  // Claimed Tx result
  const claimedSuccessTxDataRef = useRef<BridgeTxClaimedSuccess | null>(null);
  const claimedFailedTxDataRef = useRef<BridgeTxClaimedFailed | null>(null);
  const [pendingTxHash, setPendingTxHash] = useState<string | undefined>();

  const reviewModalDisclosure = useDisclosure();
  const [isMinimized, setMinimized] = useState(false);

  const bridgeMutation = useBridgeMutation({
    onSuccess: (unclaimedTxData) => {
      unclaimedSuccessTxDataRef.current = unclaimedTxData;
      unClaimedFailedTxDataRef.current = null;
      reviewModalDisclosure.onClose();
      setPendingTxHash(undefined);
    },
    onError: (txData) => {
      unClaimedFailedTxDataRef.current = txData;
      unclaimedSuccessTxDataRef.current = null;
      reviewModalDisclosure.onClose();
      setPendingTxHash(undefined);
    },
    onTxSubmitted: (txHash) => {
      setPendingTxHash(txHash);
    },
  });

  const bridgeClaimMutation = useBridgeClaimMutation({
    onSuccess: (claimedTxData) => {
      claimedSuccessTxDataRef.current = claimedTxData;
      claimedFailedTxDataRef.current = null;
    },
    onError: (txData) => {
      claimedFailedTxDataRef.current = txData;
      claimedSuccessTxDataRef.current = null;
    },
  });

  function onReview(bridgeFormData: BridgeFormData) {
    bridgeFormDataRef.current = bridgeFormData;
    reviewModalDisclosure.onOpen();
  }

  async function onBridge() {
    if (!bridgeFormDataRef.current) {
      throw new Error("Form data not available");
    }

    await bridgeMutation.mutateAsync({
      ...bridgeFormDataRef.current,
      startWaiting: () => {
        setMinimized(false);
        reviewModalDisclosure.onClose();
      },
    });
  }

  async function onClaim(claimData: BridgeClaimParams) {
    bridgeMutation.reset();
    bridgeClaimMutation.mutate(claimData);
  }

  const shouldShowBridgeWaiting = !reviewModalDisclosure.isOpen && bridgeMutation.status === "pending";
  const shouldShowBridgeSuccess = bridgeMutation.status === "success";
  const shouldShowBridgeFailed = bridgeMutation.status === "error";

  return (
    <>
      <BridgeForm onReview={onReview} />

      <BridgeReviewModal
        isOpen={reviewModalDisclosure.isOpen}
        onClose={reviewModalDisclosure.onClose}
        bridgeFormData={bridgeFormDataRef.current}
        onBridge={onBridge}
        status={bridgeMutation.status}
      />

      <BridgeWaitingModal
        isOpen={shouldShowBridgeWaiting && !isMinimized}
        onClose={() => setMinimized(true)}
        txHash={pendingTxHash}
        bridgeType={bridgeFormDataRef.current?.bridgeType}
      />

      {shouldShowBridgeWaiting && isMinimized && (
        <BridgeWaitingToast
          bridgeType={bridgeFormDataRef.current?.bridgeType}
          txHash={pendingTxHash}
          onDismiss={() => setMinimized(false)}
        />
      )}

      <BridgeSuccessModal
        isOpen={shouldShowBridgeSuccess && !isMinimized}
        onClose={() => {
          bridgeMutation.reset();
        }}
        unclaimedTxData={unclaimedSuccessTxDataRef.current}
        onClaim={onClaim}
      />

      {shouldShowBridgeSuccess && isMinimized && (
        <BridgeSuccessToast
          onDismiss={() => {
            bridgeMutation.reset();
            setMinimized(false);
          }}
          unclaimedTxData={unclaimedSuccessTxDataRef.current}
          onClaim={onClaim}
        />
      )}

      <BridgeFailedModal
        isOpen={shouldShowBridgeFailed}
        onClose={() => {
          bridgeMutation.reset();
        }}
        failedTxData={unClaimedFailedTxDataRef.current}
      />

      <ClaimWaitingModal
        isOpen={bridgeClaimMutation.status === "pending"}
        unclaimedTxData={unclaimedSuccessTxDataRef.current}
      />

      <ClaimSuccessModal
        isOpen={bridgeClaimMutation.status === "success"}
        onClose={() => {
          bridgeClaimMutation.reset();
          bridgeMutation.reset();
        }}
        claimedTxData={claimedSuccessTxDataRef.current}
      />

      <ClaimFailedModal
        isOpen={bridgeClaimMutation.status === "error"}
        onClose={() => {
          bridgeClaimMutation.reset();
          bridgeMutation.reset();
        }}
        failedTxData={claimedFailedTxDataRef.current}
      />
    </>
  );
}
